import type { SubEnvironment } from '@change-corgi/config/environments';

type TypesenseNode = {
	host: string;
	protocol: string;
	port: number;
	path: string;
};

export type TypesenseConfig = {
	apiKey: string;
	nearestNode?: TypesenseNode;
	nodes: TypesenseNode[];
};

// deepcode ignore HardcodedNonCryptoSecret: client-side token that is restricted to our domains.
const demoApiKey =
	'cVI1Q3kwbndNOVJ6ZU93ZmhBUlEzc0pzNzA0c1ZzNTNheWhQUzh2R09RTT1TVFc2eyJmaWx0ZXJfYnkiOiJkaXNjb3ZlcmFibGU6dHJ1ZSIsImV4Y2x1ZGVfZmllbGRzIjoiY2FtcGFpZ25fdGVhbV9uYW1lIn0=';

const apiKeys = {
	staging:
		'NEZHYlBPakV4NkEwQVZlUDM4eHZOWXA0dTJnbGtYTzFYaU91WktmT0lNMD1ZbmtBeyJmaWx0ZXJfYnkiOiJkaXNjb3ZlcmFibGU6dHJ1ZSIsImV4Y2x1ZGVfZmllbGRzIjoiY2FtcGFpZ25fdGVhbV9uYW1lIn0=',
	production:
		'Mm5jam83L2h4SVVnR01qOHhHcU1kUHl5SW94TzdQM3pMK2pwQTUzTVJXOD04VzVXeyJmaWx0ZXJfYnkiOiJkaXNjb3ZlcmFibGU6dHJ1ZSIsImV4Y2x1ZGVfZmllbGRzIjoiY2FtcGFpZ25fdGVhbV9uYW1lIn0=',
	jade: demoApiKey,
	teal: demoApiKey,
	noir: demoApiKey,
	plum: demoApiKey,
	vert: demoApiKey,
	cyan: demoApiKey,
	opal: demoApiKey,
	orion: demoApiKey,
};

const domains = {
	staging: 'www.staging-change.org',
	production: 'www.change.org',
	jade: 'www.jade.changeeng.org',
	teal: 'www.teal.changeeng.org',
	noir: 'www.noir.changeeng.org',
	plum: 'www.plum.changeeng.org',
	vert: 'www.vert.changeeng.org',
	cyan: 'www.cyan.changeeng.org',
	opal: 'www.opal.changeeng.org',
	orion: 'www.orion.changeeng.org',
};

function genTypesenseConfig(environment: Exclude<SubEnvironment, 'development'>): TypesenseConfig {
	return {
		apiKey: apiKeys[environment],
		nearestNode: {
			host: domains[environment],
			protocol: 'https',
			port: 443,
			path: '/ts',
		},
		nodes: [0, 1, 2].map((index) => ({
			host: domains[environment],
			protocol: 'https',
			port: 443,
			path: `/ts-${index}`,
		})),
	};
}

const typesenseConfig: Readonly<Record<SubEnvironment, TypesenseConfig>> = {
	staging: genTypesenseConfig('staging'),
	production: genTypesenseConfig('production'),
	cyan: genTypesenseConfig('cyan'),
	jade: genTypesenseConfig('jade'),
	noir: genTypesenseConfig('noir'),
	orion: genTypesenseConfig('orion'),
	plum: genTypesenseConfig('plum'),
	teal: genTypesenseConfig('teal'),
	vert: genTypesenseConfig('vert'),
	opal: genTypesenseConfig('opal'),
	// See: https://github.com/typesense/typesense-instantsearch-adapter?tab=readme-ov-file#with-instantsearchjs
	development: {
		apiKey: 'xyz',
		nodes: [
			{
				host: 'change-typesense',
				protocol: 'http',
				port: 8108,
				path: '',
			},
		],
	},
};

export function getTypesenseConfig(environment: SubEnvironment): TypesenseConfig {
	if (!isSupportedTypesenseEnvironment(environment)) {
		// probably in a personalized demo environment (e.g. jlou that points to cyan)
		throw new Error(`Unsupported environment: ${environment}`);
	}
	return typesenseConfig[environment];
}

export function isSupportedTypesenseEnvironment(environment: SubEnvironment): boolean {
	return environment in typesenseConfig;
}

export function decorateForDevProxying(config: TypesenseConfig, location: Location): TypesenseConfig {
	// in dev when proxying to staging/demo, we want to use the current hostname and port
	// (proxying to the target env is done server-side or within webpack-dev-server)
	const { hostname, port, protocol } = location;
	return {
		...config,
		nearestNode: config.nearestNode
			? {
					...config.nearestNode,
					host: hostname,
					port: parseInt(port, 10),
					protocol: protocol.replace(/:$/, ''),
				}
			: undefined,
		nodes: config.nodes.map((node) => ({
			...node,
			host: hostname,
			port: parseInt(port, 10),
			protocol: protocol.replace(/:$/, ''),
		})),
	};
}
